/**
 * The Sydney core team — the single source of truth for who is on the
 * team and what to call them.
 *
 * Invited team members sign in through Clerk but rarely fill in a name,
 * so their account otherwise surfaces only an email address.
 * `rosterDisplayName` maps that email back to the name Adi gave from the
 * planning sheet, so "Sent by" on Emails, Website messages, the feedback
 * board and Team access shows people rather than addresses; the admin
 * invite screen offers the same list.
 *
 * Pure data + a pure lookup, deliberately free of any Worker-only
 * imports so the browser bundle can pull it in too.
 */
import type { Chapter } from "./chapters";
import { lookup } from "./lookup";

export interface RosterEntry {
  name: string;
  email: string;
  /** What the team actually calls them, when it differs from the name. */
  alias?: string;
}

export const SYDNEY_CORE_TEAM: RosterEntry[] = [
  { name: "Anikait", email: "anikaitkumarus@gmail.com", alias: "Deepu" },
  { name: "Anita", email: "Anita.kumarus@gmail.com" },
  { name: "Manoj", email: "sinhamanoj@hotmail.com", alias: "Manoji" },
  { name: "Heena", email: "nirupamasinha1@gmail.com" },
  { name: "Anish", email: "anishsinha2003@gmail.com" },
  { name: "Suchitra", email: "Suchikumar.sk@gmail.com" },
  { name: "Amitabh", email: "Amit.kres1@gmail.com" },
  { name: "Sanjay", email: "Skr07@hotmail.com" },
  { name: "Raj", email: "Agrawalrajkumar@yahoo.com", alias: "Rajji" },
  { name: "Rachit", email: "Agra.rachit@gmail.com" },
  { name: "Sangeeta", email: "sangeeta.cstt@gmail.com" },
  { name: "Chandra", email: "chandu.sydney@gmail.com", alias: "Chandu" },
  { name: "Tripti", email: "tripti.tk17@gmail.com" },
  { name: "Pratik", email: "Krpratik39@gmail.com", alias: "Shivam" },
  { name: "Preity", email: "preity.nair925@gmail.com" },
  { name: "Alka", email: "alka.kishore1970@gmail.com" },
];

/** Known to be on the team, but we don't have an address yet. */
export const ROSTER_MISSING_EMAIL: string[] = [];

/**
 * The core team per chapter. Only Sydney has a planning-sheet roster so
 * far; the other chapters invite people by typing their address on the
 * Roles screen, and their lists fill in here once the organisers send
 * one. Keyed by chapter id so a Singapore build never offers to invite
 * the Australian team.
 */
const CORE_TEAM_BY_CHAPTER = {
  aus: SYDNEY_CORE_TEAM,
} satisfies Partial<Record<Chapter, readonly RosterEntry[]>>;

/** The known core team for a chapter; empty when none has been recorded. */
export function coreTeamFor(chapter: Chapter): readonly RosterEntry[] {
  return lookup(CORE_TEAM_BY_CHAPTER, chapter) ?? [];
}

const NAME_BY_EMAIL = new Map(
  SYDNEY_CORE_TEAM.map((entry) => [entry.email.toLowerCase(), entry.name]),
);

/**
 * The team name for an email, if we know it — matched case-insensitively.
 * Returns null for anyone not on the roster so callers can fall back to
 * their own default (usually the email itself).
 */
export function rosterDisplayName(
  email: string | null | undefined,
): string | null {
  if (!email) {
    return null;
  }
  return NAME_BY_EMAIL.get(email.toLowerCase()) ?? null;
}
